import React, { useCallback } from 'react'
import { noop, intersection, union } from 'lodash-es'
import { Field } from '../inputs'
import { InputContainer } from '../layout'
import { TextButtonType } from '../buttons'
import { Combobox } from './Combobox'
import { ListboxOptions, OptionKey, OptionTemplateProps } from './Listbox'
import {
  DropdownOption,
  SelectButton,
  SelectedOption,
  RemoveItemButton,
  MultiComboboxWrapper,
  SelectedListbox,
  ClearButton,
  DropdownListbox,
  DropdownTitle
} from './optionsStyles'
import { Locale, Translation } from 'locale'
import { iconSet } from '../icons'
import { ValidationIssue } from 'api'

export interface OptionGroup<K extends OptionKey> {
  id: string
  name: string
  members: K[]
}

export interface GroupedMultiComboboxProps<T, K extends OptionKey> extends ListboxOptions<T, K> {
  id?: string
  className?: string
  title?: string
  placeholder?: string
  value: K[]
  optionGroups: OptionGroup<K>[]
  groupTitle?: string
  withPortal?: boolean
  noGroupsText?: string
  readOnly?: boolean
  disabled?: boolean
  canClear?: boolean
  clearButtonLabel?: string
  onUpdate: (optionKey: K[], ...rest: any[]) => void
  dataTestId?: string
  validationIssues?: ValidationIssue[] | undefined
}

export const GroupedMultiCombobox: React.FunctionComponent<GroupedMultiComboboxProps<any, OptionKey>> = props => {
  const {
    className,
    value,
    title,
    optionKeys,
    optionGroups,
    groupTitle,
    noGroupsText,
    disabled,
    readOnly,
    optionColorPalette,
    canClear,
    clearButtonLabel,
    onUpdate,
    dataTestId,
    withPortal,
    ...rest
  } = props

  const handleAddOptions = useCallback(
    (keys: OptionKey[]) => {
      const newValue = union(value, keys)
      if (newValue.length !== value.length) {
        onUpdate(newValue)
      }
    },
    [value, onUpdate]
  )

  const handleDeleteOptions = useCallback(
    (keys: OptionKey[]) => {
      const newValue = value.filter(key => !keys.includes(key))
      onUpdate(newValue)
    },
    [value, onUpdate]
  )

  const toggleOptionsSelected = useCallback(
    (keys: OptionKey[], selected: boolean, evt: React.MouseEvent<HTMLElement>) => {
      if (selected) {
        handleDeleteOptions(keys)
      } else {
        handleAddOptions(keys)
      }

      evt.preventDefault()
      evt.stopPropagation()
    },
    [handleDeleteOptions, handleAddOptions]
  )

  const handleClearOptions = useCallback(() => {
    onUpdate([])
  }, [onUpdate])

  const handleGroupDropdownUpdate = useCallback(
    (groupKey: OptionKey) => {
      const optionGroup = optionGroups.find(group => group.id === groupKey)
      if (optionGroup) {
        handleAddOptions(optionGroup.members)
      }
    },
    [optionGroups, handleAddOptions]
  )

  const renderGroupDropdownOption = useCallback(
    (optionProps: OptionTemplateProps<any, OptionKey>) => {
      const optionGroup = optionGroups.find(group => group.id === optionProps.optionKey)
      const optionSelected = !!optionGroup && intersection(value, optionGroup.members).length === optionGroup.members.length

      return (
        <DropdownOption {...optionProps}>
          <span>{optionProps.optionText}</span>
          <SelectButton
            type={TextButtonType.Primary}
            selected={optionSelected}
            canFocus={false}
            onClick={(evt: React.MouseEvent<HTMLElement>) =>
              optionGroup ? toggleOptionsSelected(optionGroup.members, optionSelected, evt) : noop
            }>
            {optionSelected ? iconSet.tickIcon : Locale.translate(Translation.TC_BUTTON_ADD)}
          </SelectButton>
        </DropdownOption>
      )
    },
    [optionGroups, value, toggleOptionsSelected]
  )

  const renderDropdownOption = useCallback(
    (optionProps: OptionTemplateProps<any, OptionKey>) => {
      const optionSelected = value.includes(optionProps.optionKey)

      return (
        <DropdownOption {...optionProps}>
          <span>{optionProps.optionText}</span>
          <SelectButton
            type={TextButtonType.Primary}
            selected={optionSelected}
            canFocus={false}
            onClick={(evt: React.MouseEvent<HTMLElement>) => toggleOptionsSelected([optionProps.optionKey], optionSelected, evt)}>
            {optionSelected ? iconSet.tickIcon : Locale.translate(Translation.TC_BUTTON_ADD)}
          </SelectButton>
        </DropdownOption>
      )
    },
    [value, toggleOptionsSelected]
  )

  const renderSelectedOption = useCallback(
    (optionProps: OptionTemplateProps<any, OptionKey>) => {
      const showRemoveButton = !disabled && !readOnly

      return (
        <SelectedOption {...optionProps}>
          <span>{optionProps.optionText}</span>
          {showRemoveButton && (
            <RemoveItemButton onClick={() => handleDeleteOptions([optionProps.optionKey])}>{iconSet.crossIcon}</RemoveItemButton>
          )}
        </SelectedOption>
      )
    },
    [disabled, readOnly, handleDeleteOptions]
  )

  return (
    <MultiComboboxWrapper className={className}>
      {!readOnly && (
        <InputContainer data-test-id={`${dataTestId}-InputContainer`}>
          <Combobox
            {...rest}
            withPortal={withPortal}
            title={title && <DropdownTitle>{title}</DropdownTitle>}
            optionKeys={optionKeys}
            value={null}
            secondaryDropdown={
              <DropdownListbox
                title={groupTitle && <DropdownTitle>{groupTitle}</DropdownTitle>}
                optionKeys={optionGroups.map(group => group.id)}
                getOptionText={(group: OptionGroup<OptionKey>) => group.name}
                getOption={groupKey => optionGroups.find(group => group.id === groupKey)}
                filterOption={(_groupKey: OptionKey, group: OptionGroup<OptionKey>) =>
                  !!group && intersection(group.members, optionKeys).length === group.members.length
                }
                optionTemplate={renderGroupDropdownOption}
                noItemsText={noGroupsText || Locale.translate(Translation.SC_N_GROUPS, { count: 0 })}
                onUpdate={handleGroupDropdownUpdate}
              />
            }
            disabled={disabled}
            onUpdate={optionKey => handleAddOptions([optionKey])}
            optionTemplate={renderDropdownOption}
          />
        </InputContainer>
      )}

      {value.length > 0 && (
        <>
          <SelectedListbox
            optionKeys={value}
            getOptionText={props.getOptionText}
            getOption={props.getOption}
            optionTemplate={renderSelectedOption}
            optionColorPalette={optionColorPalette}
            onOptionDoubleClick={disabled || readOnly ? noop : optionKey => handleDeleteOptions([optionKey])}
          />
          {canClear && (
            <ClearButton disabled={value.length === 0 || readOnly || disabled} onClick={handleClearOptions}>
              {clearButtonLabel || Locale.translate(Translation.SC_BUTTON_CLEAR_ALL)}
            </ClearButton>
          )}
        </>
      )}
    </MultiComboboxWrapper>
  )
}

export const GroupedMultiComboboxField = Field(GroupedMultiCombobox)
